/**
 * Synchronisation temps réel session / trésorerie / enrôlement (même poste financier).
 * Pas de polling agressif : rechargement serveur sur événements WS, reconnexion et visibility.
 */
import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getWebSocketBaseUrl } from '../config/api.config';
import apiService from '../services/api';
import { getOrCreateDeviceId } from '../offline/deviceIdentity';
import { ensureAssistanceSessionFresh, peekAssistanceSession } from '../utils/assistanceSession';

function getAdminUser() {
  try {
    const raw = localStorage.getItem('admin_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function usePosCaisseRealtime({
  commercantId,
  posteCaisseId,
  enabled = true,
  onSessionSync,
  onSessionClosedRemotely,
  onEnrollmentChanged,
  onDeviceRevoked,
}) {
  const socketRef = useRef(null);
  const lastSeqRef = useRef(null);
  const localDeviceId = getOrCreateDeviceId();

  const shouldIgnoreEmitter = useCallback(
    (emitterDeviceId) => emitterDeviceId && emitterDeviceId === localDeviceId,
    [localDeviceId]
  );

  const requestSync = useCallback(() => {
    const socket = socketRef.current;
    if (socket?.connected) {
      socket.emit('pos:sync_request');
    }
    onSessionSync?.({ reason: 'sync_request' });
  }, [onSessionSync]);

  useEffect(() => {
    if (!enabled || !commercantId || !posteCaisseId) {
      return undefined;
    }

    const user = getAdminUser();
    if (!user?.id) {
      return undefined;
    }

    const socket = io(getWebSocketBaseUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
    });
    socketRef.current = socket;

    const handleSessionChanged = (payload) => {
      if (payload?.commercantId !== commercantId) return;
      if (payload?.posteCaisseId !== posteCaisseId) return;
      if (payload?.seq && payload.seq === lastSeqRef.current) return;
      lastSeqRef.current = payload?.seq || null;
      if (shouldIgnoreEmitter(payload?.emitterDeviceId)) return;
      onSessionSync?.({ reason: 'session_changed', kind: payload?.kind });
      if (payload?.kind === 'CLOSE') {
        onSessionClosedRemotely?.(payload);
      }
    };

    const handleTresorerieChanged = (payload) => {
      if (payload?.commercantId !== commercantId) return;
      if (payload?.posteCaisseId !== posteCaisseId) return;
      if (shouldIgnoreEmitter(payload?.emitterDeviceId)) return;
      onSessionSync?.({ reason: 'tresorerie_changed' });
    };

    const handleEnrollmentChanged = (payload) => {
      if (payload?.commercantId !== commercantId) return;
      onEnrollmentChanged?.(payload);
      if (
        payload?.action === 'REVOKE' &&
        payload?.deviceId &&
        payload.deviceId === localDeviceId
      ) {
        onDeviceRevoked?.(payload);
        socket.disconnect();
        return;
      }
      onSessionSync?.({ reason: 'enrollment_changed' });
    };

    const authenticate = () => {
      const peeked = peekAssistanceSession();
      if (peeked && !ensureAssistanceSessionFresh()) {
        return;
      }
      const token = apiService.getAccessToken() || apiService.getSuperAdminAccessToken();
      socket.emit('pos:authenticate', {
        userId: user.id,
        token,
        commercantId,
        posteCaisseId,
        deviceId: localDeviceId,
      });
    };

    socket.on('connect', () => {
      authenticate();
    });

    socket.on('pos:authenticated', () => {
      requestSync();
    });

    socket.on('pos:sync_hint', () => {
      onSessionSync?.({ reason: 'sync_hint' });
    });

    socket.on('pos:session_changed', handleSessionChanged);
    socket.on('pos:tresorerie_changed', handleTresorerieChanged);
    socket.on('pos:enrollment_changed', handleEnrollmentChanged);

    const onVisibility = () => {
      if (!document.hidden) {
        requestSync();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      socket.off('pos:session_changed', handleSessionChanged);
      socket.off('pos:tresorerie_changed', handleTresorerieChanged);
      socket.off('pos:enrollment_changed', handleEnrollmentChanged);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [
    enabled,
    commercantId,
    posteCaisseId,
    localDeviceId,
    onSessionSync,
    onSessionClosedRemotely,
    onEnrollmentChanged,
    onDeviceRevoked,
    requestSync,
    shouldIgnoreEmitter,
  ]);

  return { localDeviceId, requestSync };
}
